/* jshint esversion: 6 */

/**
 * APIService class provides methods to interact with various APIs.
 * It uses AngularJS services for HTTP requests, translation, location, etc.
 */
class APIService {
    /**
     * Constructor for APIService.
     * @param {Object} $http - AngularJS HTTP service.
     * @param {Object} $translate - AngularJS translation service.
     * @param {Object} $location - AngularJS location service.
     * @param {Object} $injector - AngularJS injector service.
     * @param {Object} $q - AngularJS promise service.
     * @param {Object} $config - Configuration object.
     * @param {Object} $sessionStorage - Session storage service.
     */
    constructor($http, $translate, $location, $injector, $q, $config, $sessionStorage) {
        this.$http = $http;
        this.$translate = $translate;
        this.$location = $location;
        this.$injector = $injector;
        this.$q = $q;
        this.$config = $config;
        this.$sessionStorage = $sessionStorage;

        this._logIdentifier = 'APIService';
        this.$logger = $injector.get('LogBridgeService');

        this.url = '/api/v1/';
        this.func_url = '/api/func/';
        this.wd_url = '/wrapdeploy/';
        this.export_ibis_ms = '/export-ibis-ms/';
    }

    /**
     * Fetches paper data based on filters.
     * @param {Object} filters - Filters to apply.
     * @returns {Promise} - Promise that resolves with the paper data.
     */
    getPaper(filters) {
        var filterStr = this.getFilterStr(filters);
        var response = this.$http.get(this.url + 'paper/' + filterStr).then(function(response) {
            return response.data;
        });
        return response;
    }

    /**
     * Clones an assessment by UUID.
     * @param {string} uuid - UUID of the assessment to clone.
     * @returns {Promise} - Promise that resolves with the cloned assessment data.
     */
    cloneAssessment(uuid) {
        var response = this.$http.post(this.func_url + 'clone_assessment/' + uuid + '/', {}).then(function(response) {
            return response.data;
        });
        return response;
    }

    /**
     * Deletes an assessment by UUID.
     * @param {string} uuid - UUID of the assessment to delete.
     * @returns {Promise} - Promise that resolves with the deletion response.
     */
    deleteAssessment(uuid) {
        var response = this.$http.post(this.url + 'assessment/delete/', {
            assessment_uuid: uuid
        }).then(function(response) {
            return response.data;
        }, function(error) {
            return error.data;
        });
        return response;
    }

    /**
     * Fetches a list of assessments based on filters.
     * @param {Object} filters - Filters to apply.
     * @returns {Promise} - Promise that resolves with the assessment list.
     */
    getAssessmentList(filters) {
        var fullTextSearch = '';
        if (filters['full_text_search']) {
            fullTextSearch = 'search/' + filters['full_text_search'] + "/";
            delete filters['full_text_search'];
        }
        var filterStr = this.getFilterStr(filters);
        var response = this.$http.get(this.url + 'assessment/' + fullTextSearch + filterStr).then(function(response) {
            return response.data;
        });
        return response;
    }

    /**
     * Fetches an assessment by UUID.
     * @param {string} uuid - UUID of the assessment.
     * @returns {Promise} - Promise that resolves with the assessment data.
     */
    getAssessmentByUUID(uuid) {
        var response = this.$http.get(this.url + 'assessment/uuid/' + uuid + '/').then(function(response) {
            return response.data;
        });
        return response;
    }

    /**
     * Creates a new assessment.
     * @param {Object} data - Data for the new assessment.
     * @returns {Promise} - Promise that resolves with the created assessment data.
     */
    createAssessment(data) {
        var response = this.$http.post(this.url + 'assessment/', data).then(function(response) {
            return response.data;
        }, function(error) {
            return error.data;
        });
        return response;
    }

    /**
     * Creates a new entity.
     * @param {Object} data - Data for the new entity.
     * @param {string} uuid - UUID of the entity.
     * @returns {Promise} - Promise that resolves with the created entity data.
     */
    createEntity(data, uuid) {
        var response = this.$http.post(this.url + 'entity/' + uuid + '/', data).then(function(response) {
            return response.data;
        }, function(error) {
            return error.data;
        });
        return response;
    }

    /**
     * Creates a new assessment with additional entities.
     * @param {Object} assessmentData - Data for the new assessment.
     * @returns {Promise} - Promise that resolves when the assessment and entities are created.
     */
    createNewAssessment(assessmentData) {
        var taskUUID = assessmentData.assessment_uuid;

        var future = this.$q.defer();

        var response = this.createAssessment(assessmentData).then(function(data) {
            this.getPaper({ id: data.paper_id }).then(function(data) {
                var _savingList = [];
                var _commitMessage = {};

                var title = data.objects.length > 0 ? data.objects[0].name : '';
                var questionItemGroupUUID = angular.createUUID();

                var AssessmentEntityData = {
                    "entity_uuid": taskUUID,
                    "entity_content": {
                        "uuid": taskUUID,
                        "title": title,
                        "_comment": title + " comment",
                        "sheets": [],
                        "questionItemGroups": [questionItemGroupUUID],
                        "backgroundResourceSheetTeaserOnly": false,
                        "type": "task",
                        "backgroundResourceSheet": null
                    },
                    "paper_id": taskUUID
                };
                _commitMessage[taskUUID] = {
                    action: 'created',
                    type: 'task',
                    title: title
                };
                _savingList.push(AssessmentEntityData);

                var QuestionItemGroupEntityData = {
                    "entity_uuid": questionItemGroupUUID,
                    "entity_content": {
                        uuid: questionItemGroupUUID,
                        type: "QuestionItemGroup",
                        title: this.$translate.instant("CMS.SHEET-STRUCTURE-EDITOR.QUESTION-ITEM-GROUP.DEFAULT-TITLE"),
                        default: true
                    }
                };
                _commitMessage[questionItemGroupUUID] = {
                    action: 'created',
                    type: 'QuestionItemGroup',
                    title: this.$translate.instant("CMS.SHEET-STRUCTURE-EDITOR.QUESTION-ITEM-GROUP.DEFAULT-TITLE")
                };

                _savingList.push(QuestionItemGroupEntityData);

                var requestData = {
                    savingList: _savingList,
                    commitMessages: _commitMessage
                };
                this.createEntity(requestData, taskUUID).then(function() {
                    console.log("Assessment entity created");
                    console.log("QIG created");
                    future.resolve();
                });
            }.bind(this));
        }.bind(this));

        return future.promise;
    }

    /**
     * Creates a default question item group.
     * @param {string} taskUUID - UUID of the task.
     * @param {string} questionItemGroupUUID - UUID of the question item group.
     * @returns {Promise|boolean} - Promise that resolves with the created question item group data or false if taskUUID is not provided.
     */
    createDefaultQuestionItemGroup(taskUUID, questionItemGroupUUID) {
        if (!taskUUID) {
            return false;
        }
        var QuestionItemGroupEntityData = {
            "entity_uuid": questionItemGroupUUID,
            "entity_content": {
                type: "QuestionItemGroup",
                title: this.$translate.instant("CMS.SHEET-STRUCTURE-EDITOR.QUESTION-ITEM-GROUP.DEFAULT-TITLE"),
                default: true
            }
        };
        var response = this.createEntity(QuestionItemGroupEntityData, taskUUID).then(function(response) {
            return response;
        });
        return response;
    }

    /**
     * Updates an assessment by UUID.
     * @param {string} uuid - UUID of the assessment.
     * @param {Object} data - Data to update the assessment.
     * @returns {Promise} - Promise that resolves with the updated assessment data.
     */
    updateAssessment(uuid, data) {
        var response = this.$http.post(this.url + 'assessment/update/' + uuid + '/', data).then(function(response) {
            return response.data;
        }, function(error) {
            return error.data;
        });
        return response;
    }

    /**
     * Performs versioning and locking of an assessment by UUID.
     * @param {string} uuid - UUID of the assessment.
     * @param {Object} data - Data for versioning and locking.
     * @returns {Promise|null} - Promise that resolves with the versioning and locking response or null if UUID is not provided.
     */
    versioningAndLockingAssessment(uuid, data) {
        if (!data) {
            data = {};
        }
        if (!uuid) {
            console.log("versioningAndLockingAssessment UUID null");
            return null;
        }
        var response = this.$http.post(this.url + 'assessment/versioningandlocking/' + uuid + '/', data);
        return response;
    }

    /**
     * Fetches logs by UUID and data.
     * @param {string} uuid - UUID of the logs.
     * @param {Object} data - Data for fetching logs.
     * @returns {Promise} - Promise that resolves with the logs data.
     */
    getLogs(uuid, data) {
        var future = this.$q.defer();
        this.$http.post(this.url + 'assessment/auditlog/' + uuid + '/', data).then(function(response) {
            future.resolve(response.data);
        }, function(error) {
            future.reject(error.data);
        });
        return future.promise;
    }

    /**
     * Fetches session data based on filters.
     * @param {Object} filters - Filters to apply.
     * @returns {Promise} - Promise that resolves with the session data.
     */
    getSession(filters) {
        var filterStr = this.getFilterStr(filters);
        var sessionObj = this.$http.get(this.url + 'session/' + filterStr).then(function(response) {
            sessionObj = this.getObj(response, 'session', 'session');
            return sessionObj;
        }.bind(this));
        return sessionObj;
    }

    /**
     * Fetches subject data based on filters.
     * @param {Object} filters - Filters to apply.
     * @returns {Promise} - Promise that resolves with the subject data.
     */
    getSubject(filters) {
        var filterStr = this.getFilterStr(filters);
        var subjectObj = this.$http.get(this.url + 'subject/' + filterStr).then(function(response) {
            subjectObj = this.getObj(response, 'subject', 'subject');
            return subjectObj;
        }.bind(this));
        return subjectObj;
    }

    /**
     * Fetches language data based on filters.
     * @param {Object} filters - Filters to apply.
     * @param {boolean} inpaper - Whether to include in-paper data.
     * @returns {Promise} - Promise that resolves with the language data.
     */
    getLanguage(filters, inpaper) {
        var filterStr = this.getFilterStr(filters);
        if (filterStr !== "" && inpaper) {
            filterStr += "&inpaper=1";
        } else if (inpaper) {
            filterStr = "?inpaper=1";
        }
        var subjectObj = this.$http.get(this.url + 'language/' + filterStr).then(function(response) {
            subjectObj = this.getObj(response, 'name', 'id', { isoCodeShort: 'iso_code_short', core: 'core', writing_direction: 'writing_direction', full_name: 'full_name', locale: 'locale' });
            return subjectObj;
        }.bind(this));
        return subjectObj;
    }

    /**
     * Fetches component data based on filters.
     * @param {Object} filters - Filters to apply.
     * @returns {Promise} - Promise that resolves with the component data.
     */
    getComponent(filters) {
        var filterStr = this.getFilterStr(filters);
        var subjectObj = this.$http.get(this.url + 'component/' + filterStr).then(function(response) {
            subjectObj = this.getObj(response, 'component', 'component');
            return subjectObj;
        }.bind(this));
        return subjectObj;
    }

    /**
     * Constructs a filter string from filters.
     * @param {Object} filters - Filters to apply.
     * @returns {string} - Filter string.
     */
    getFilterStr(filters) {
        var filterStr = "";

        if (typeof filters !== 'object') {
            return filterStr;
        }
        angular.forEach(filters, function(value, filter) {
            if (typeof value !== 'undefined' && value !== null && value !== "") {
                filterStr += filter + "=" + value;
                filterStr += "&";
            }
        });

        if (filterStr.length > 0) {
            return "?" + filterStr.substring(0, filterStr.length - 1);
        }

        return filterStr;
    }

    /**
     * Constructs an object from response data.
     * @param {Object} response - Response data.
     * @param {string} labelName - Label name.
     * @param {string} filterName - Filter name.
     * @param {Object} other - Other properties to include.
     * @returns {Array} - Array of objects.
     */
    getObj(response, labelName, filterName, other) {
        var obj = [];
        var testObj = {};
        angular.forEach(response.data.objects, function(value, index) {
            if (!testObj[value[labelName]] && typeof testObj[value[labelName]] === 'undefined') {
                testObj[value[labelName]] = index;
                if (other && angular.isObject(other)) {
                    var _obj = {
                        label: value[labelName],
                        index: value[filterName]
                    };
                    angular.forEach(other, function(otherVal, otherKey) {
                        _obj[otherKey] = value[otherVal];
                    });
                    obj.push(_obj);
                } else {
                    obj.push({
                        label: value[labelName],
                        index: value[filterName]
                    });
                }
            }
        });
        return obj;
    }

    /**
     * Fetches assessment type data based on filters.
     * @param {Object} filters - Filters to apply.
     * @returns {Promise} - Promise that resolves with the assessment type data.
     */
    getAssessmentType(filters) {
        var filterStr = this.getFilterStr(filters);
        var assessmenttypeObj = this.$http.get(this.url + 'assessmenttype/' + filterStr).then(function(response) {
            assessmenttypeObj = this.getObj(response, 'name', 'id');
            return assessmenttypeObj;
        }.bind(this));
        return assessmenttypeObj;
    }

    /**
     * Sets the assessment type.
     * @param {string} value - Value of the assessment type.
     * @returns {Promise} - Promise that resolves with the set assessment type response.
     */
    setAssessmentType(value) {
        var data = {
            name: value
        };

        var response = this.$http.post(this.url + 'assessmenttype/', data).then(function(response) {
            return response.data;
        }, function(error) {
            return error.data;
        });
        return response;
    }

    /**
     * Retrieves the entity list for a given task.
     * @param {string} task - The task identifier.
     * @param {boolean} full - Flag to determine if the full entity list should be retrieved.
     * @returns {Promise} - Promise that resolves with the entity list response.
     */
    getEntityList(task, full) {
        var full_option = full ? full : false,
            full_url = full_option ?
            this.url + 'entity/' + task + '/?full' + (this.$config.isPDFMode() ? '&ispdfmode' : '') :
            this.url + 'entity/' + task + '/';

        var response = this.$http.get(full_url)
            .then(function(response) {
                return response.data;
            }, function(error) {
                return error.data;
            });
        return response;
    }

    /**
     * Deletes an entity for a given task.
     * @param {string} task - The task identifier.
     * @param {string} entity - The entity identifier.
     * @returns {Promise} - Promise that resolves with the delete entity response.
     */
    deleteEntity(task, entity) {
        var full_url = this.url + 'entity/' + task + '/' + entity + '/';
        return this.$http.delete(full_url);
    }

    /**
     * Renames an entity for a given task.
     * @param {string} task - The task identifier.
     * @param {string} source - The source entity identifier.
     * @param {string} target - The target entity identifier.
     * @returns {Promise} - Promise that resolves with the rename entity response.
     */
    renameEntity(task, source, target) {
        var full_url = this.url + 'entity/' + task + '/' + source + '/' + target + '/';
        return this.$http.put(full_url);
    }

    /**
     * Retrieves a secure link for a given task and media UUID.
     * @param {string} task - The task identifier.
     * @param {string} mediaUUID - The media UUID.
     * @returns {Promise} - Promise that resolves with the secure link response.
     */
    getSecureLink(task, mediaUUID) {
        var full_url = this.url + 'getmediasecurelink/' + task + '/' + mediaUUID + '/';
        return this.$http.get(full_url);
    }

    /**
     * Publishes a specimen package for a given data and ID.
     * @param {Object} data - The data to be published.
     * @param {string} id - The identifier for the package.
     * @returns {Promise} - Promise that resolves with the publish specimen package response.
     */
    publishSpecimenPackage(data, id) {
        var response = this.$http.post(this.wd_url + 'build_specimen_package/' + id + '/', data).then(function(response) {
            return response.data;
        }, function(error) {
            return error.data;
        });
        return response;
    }

    /**
     * Publishes a media package for a given data and ID.
     * @param {Object} data - The data to be published.
     * @param {string} id - The identifier for the package.
     * @returns {Promise} - Promise that resolves with the publish media package response.
     */
    publishMediaPackage(data, id) {
        var response = this.$http.post(this.wd_url + 'build_media_package/' + id + '/', data).then(function(response) {
            return response.data;
        }, function(error) {
            return error.data;
        });
        return response;
    }

    /**
     * Publishes a school package for a given data and ID.
     * @param {Object} data - The data to be published.
     * @param {string} id - The identifier for the package.
     * @returns {Promise} - Promise that resolves with the publish school package response.
     */
    publishSchoolPackage(data, id) {
        var response = this.$http.post(this.wd_url + 'build_school_package/' + id + '/', data).then(function(response) {
            return response.data;
        }, function(error) {
            return error.data;
        });
        return response;
    }

    /**
     * Publishes an examiner package for a given data and ID.
     * @param {Object} data - The data to be published.
     * @param {string} id - The identifier for the package.
     * @returns {Promise} - Promise that resolves with the publish examiner package response.
     */
    publishExaminerPackage(data, id) {
        var response = this.$http.post(this.wd_url + 'build_examiner_package/' + id + '/', data).then(function(response) {
            return response.data;
        }, function(error) {
            return error.data;
        });
        return response;
    }

    /**
     * Rebuilds a package for a given ID.
     * @param {string} id - The identifier for the package.
     * @returns {Promise} - Promise that resolves with the rebuild package response.
     */
    rebuildPackage(id) {
        var response = this.$http.post(this.wd_url + 'rebuild_package/' + id + '/').then(function(response) {
            return response.data;
        }, function(error) {
            return error.data;
        });
        return response;
    }

    /**
     * Checks if a school package is valid for a given ID.
     * @param {string} id - The identifier for the package.
     * @returns {Promise} - Promise that resolves with the school package validation response.
     */
    isSchoolPackageValid(id) {
        var response = this.$http.get(this.wd_url + 'validate_school_package/' + id + '/').then(function(response) {
            return response.data;
        }, function(error) {
            return error.data;
        });
        return response;
    }

    /**
     * Checks if an examiner package is valid for a given ID.
     * @param {string} id - The identifier for the package.
     * @returns {Promise} - Promise that resolves with the examiner package validation response.
     */
    isExaminerPackageValid(id) {
        var response = this.$http.get(this.wd_url + 'validate_examiner_package/' + id + '/').then(function(response) {
            return response.data;
        }, function(error) {
            return error.data;
        });
        return response;
    }

    /**
     * Retrieves the publication by assessment ID for given filters.
     * @param {Object} filters - The filters to apply.
     * @returns {Promise} - Promise that resolves with the publication by assessment ID response.
     */
    getPublicationByAssessmentID(filters) {
        var filterStr = this.getFilterStr(filters);
        var response = this.$http.get(this.wd_url + 'api/v1/publication/' + filterStr + '').then(function(response) {
            return response.data;
        }, function(error) {
            return error.data;
        });
        return response;
    }

    /**
     * Updates the publication by ID for given data and ID.
     * @param {Object} data - The data to be updated.
     * @param {string} id - The identifier for the publication.
     * @returns {Promise} - Promise that resolves with the update publication response.
     */
    updatePublicationByID(data, id) {
        var response = this.$http.post(this.wd_url + 'api/v1/publication/update/' + id + '/', data).then(function(response) {
            return response.data;
        }, function(error) {
            return error.data;
        });
        return response;
    }

    /**
     * Retrieves the package bundle job by publication ID for given filters.
     * @param {Object} filters - The filters to apply.
     * @returns {Promise} - Promise that resolves with the package bundle job response.
     */
    getPackagebundleJobByPublicationID(filters) {
        var response;
        if (filters['full_text_search'] && false) {
            var fullTextSearch = filters['full_text_search'];
            delete filters['full_text_search'];
            var filterStr = this.getFilterStr(filters);
            response = this.$http.post(
                this.wd_url + 'api/v1/packagebundle_job/' + filterStr, { search: fullTextSearch }).then(function(response) {
                return response.data;
            }, function(error) {
                return error.data;
            });
        } else {
            var filterStr = this.getFilterStr(filters);
            response = this.$http.get(this.wd_url + 'api/v1/packagebundle_job/' + filterStr + '').then(function(response) {
                return response.data;
            }, function(error) {
                return error.data;
            });
        }
        return response;
    }

    /**
     * Retrieves the WD assessment for given filters.
     * @param {Object} filters - The filters to apply.
     * @returns {Promise} - Promise that resolves with the WD assessment response.
     */
    getWDAssessment(filters) {
        var fullTextSearch = '';
        var response;
        if (filters['full_text_search']) {
            var filterStr = this.getFilterStr(filters);
            response = this.$http.get(this.wd_url + 'api/v1/assessment/search/' + filterStr).then(function(response) {
                return response.data;
            });
        } else {
            var newFilterStr = this.getFilterStr(filters);
            response = this.$http.get(this.wd_url + 'api/v1/assessment/' + fullTextSearch + newFilterStr).then(function(response) {
                return response.data;
            });
        }
        return response;
    }

    /**
     * Retrieves the WD assessment by UUID.
     * @param {string} uuid - The UUID of the assessment.
     * @returns {Promise} - Promise that resolves with the WD assessment by UUID response.
     */
    getWDAssessmentByUUID(uuid) {
        var response = this.$http.get(this.wd_url + 'api/v1/assessment/' + uuid + '/').then(function(response) {
            return response.data;
        });
        return response;
    }

    /**
     * Retrieves the WD subject.
     * @returns {Promise} - Promise that resolves with the WD subject response.
     */
    getWDSubject() {
        var subjectObj = this.$http.get(this.wd_url + 'api/v1/subject/').then(function(response) {
            subjectObj = this.getObj(response, 'subject', 'subject');
            return subjectObj;
        }.bind(this));
        return subjectObj;
    }

    /**
     * Retrieves the WD language.
     * @returns {Promise} - Promise that resolves with the WD language response.
     */
    getWDLanguage() {
        var languageObj = this.$http.get(this.wd_url + 'api/v1/language/').then(function(response) {
            languageObj = this.getObj(response, 'name', 'id');
            return languageObj;
        }.bind(this));
        return languageObj;
    }

    /**
     * Retrieves the WD type.
     * @returns {Promise} - Promise that resolves with the WD type response.
     */
    getWDType() {
        var typeObj = this.$http.get(this.wd_url + 'api/v1/type/').then(function(response) {
            typeObj = this.getObj(response, 'name', 'id');
            return typeObj;
        }.bind(this));
        return typeObj;
    }

    /**
     * Retrieves the WD bundle status for a given publication ID.
     * @param {string} publicationId - The publication ID.
     * @returns {Promise} - Promise that resolves with the WD bundle status response.
     */
    getWDBudleStatus(publicationId) {
        var typeObj = this.$http.get(this.wd_url + 'api/v1/bundlestatus/?publication=' + publicationId).then(function(response) {
            typeObj = this.getObj(response, 'status', 'status');
            return typeObj;
        }.bind(this));
        return typeObj;
    }

    /**
     * Retrieves the mark scheme assessment for given parameters.
     * @param {string} no_lang_code - The language code.
     * @param {string} session - The session identifier.
     * @returns {Promise} - Promise that resolves with the mark scheme assessment response.
     */
    getMarkSchemeAssessment(no_lang_code, session) {
        var deferred = this.$q.defer(),
            url = this.export_ibis_ms + 'mark-scheme-assessment/';

        if (!no_lang_code || !session) {
            deferred.reject("Error. Missing parameter. no_lang_code", no_lang_code, "session", session);
        }

        var full_url = url + no_lang_code + '/' + session + '/';

        this.$http.get(full_url).then(
            function (success) {
                deferred.resolve(success);
            },
            function (error) {
                console.error('getMarkSchemeAssessment Error!', error);
                deferred.reject("Could not get Mark Scheme Assessment using", no_lang_code, session);
            }
        );
        return deferred.promise;
    }

    /**
     * Retrieves the export IBIS MS assessment list for given filters.
     * @param {Object} filters - The filters to apply.
     * @returns {Promise} - Promise that resolves with the export IBIS MS assessment list response.
     */
    getExportIbisMSAssessmentList(filters) {
        var deferred = this.$q.defer(),
            url = this.export_ibis_ms + 'api/v1/assessment/',
            fullTextSearch = '',
            filterStr;

        if (filters['full_text_search']) {
            fullTextSearch = 'search/' + filters['full_text_search'];
            delete filters['full_text_search'];
            filterStr = this
filterStr = this.getFilterStr(filters);
        } else {
            filterStr = this.getFilterStr(filters);
        }

        this.$http.get(url + fullTextSearch + filterStr).then(
            function (success) {
                deferred.resolve(success);
            },
            function (error) {
                console.error('getExportIbisMSAssessment Error!', error);
                deferred.reject("Could not get Ibis Assessments");
            }
        );
        return deferred.promise;
    }

    /**
     * Publishes the IBIS MS for given parameters.
     * @param {string} no_lang_code - The language code.
     * @param {string} session - The session identifier.
     * @param {boolean} revoke - Flag to determine if the publication should be revoked.
     * @returns {Promise} - Promise that resolves with the publish IBIS MS response.
     */
    publishIbisMS(no_lang_code, session, revoke) {
        var deferred = this.$q.defer(),
            url = this.export_ibis_ms + 'mark-scheme-publish/';

        if (!no_lang_code || !session) {
            deferred.reject("Error. Missing parameter. no_lang_code", no_lang_code, "session", session);
        }

        if (!angular.isDefined(revoke)) {
            revoke = false;
        }

        var full_url = url + no_lang_code + '/' + session + '/';

        var config = {
            params: {
                revoke: revoke ? 1 : 0
            }
        };

        this.$http.get(full_url, config).then(
            function (success) {
                deferred.resolve(success);
            },
            function (error) {
                console.error('publishIbisMS Error!', error);
                deferred.reject("Could not export Mark Scheme using", no_lang_code, session);
            }
        );
        return deferred.promise;
    }

    /**
     * Retrieves the export IBIS MS by UUID.
     * @param {string} uuid - The UUID of the assessment.
     * @returns {Promise} - Promise that resolves with the export IBIS MS by UUID response.
     */
    getExportIbisMSByUUID(uuid) {
        var deferred = this.$q.defer(),
            url = this.export_ibis_ms + 'mark-scheme-by-uuid/';
        if (!uuid) {
            deferred.reject("UUID Expected. Received", typeof uuid);
        } else if (!angular.isUUID(uuid, true)) {
            deferred.reject("Given UUID is not UUID-4 compliant!", uuid);
        }

        var full_url = url + uuid + '/';

        this.$http.get(full_url).then(
            function (success) {
                deferred.resolve(success);
            },
            function (error) {
                console.error('getExportIbisMSByUUID Error!', error);
                deferred.reject("Could not get data from Server.", error);
            }
        );
        return deferred.promise;
    }

    /**
     * Retrieves the mark scheme structure.
     * @param {string} no_lang_code - The language code.
     * @param {string} session - The session identifier.
     * @returns {Promise} - Promise that resolves with the mark scheme structure response.
     */
    getMarkSchemeStructure(no_lang_code, session) {
        var deferred = this.$q.defer(),
            url = this.export_ibis_ms + 'api/v1/elements/';

        if (!no_lang_code || !session) {
            deferred.reject("Error. Missing parameter. no_lang_code", no_lang_code, "session", session);
        }

        var month, year, reverse = true;
        month = parseInt(session.substr(-2));
        year = parseInt(session.substr(0, 4));

        var config = {
            params: {
                no_lang_code: no_lang_code,
                year: year,
                month: angular.getMonthFromName(month, reverse)
            }
        };

        this.$http.get(url, config).then(
            function (success) {
                deferred.resolve(success);
            },
            function (error) {
                console.error('publishIbisMS Error!', error);
                deferred.reject("Could not export Mark Scheme using", no_lang_code, session);
            }
        );
        return deferred.promise;
    }

    /**
     * Retrieves the response data.
     * @returns {Promise} - Promise that resolves with the response data.
     */
    getResponseData() {
        var defer = this.$q.defer();
        if (this.$sessionStorage.response_data_path) {
            this.$http.get("/admintools/responsefile/" + this.$sessionStorage.response_data_path).success(function(data, status) {
                defer.resolve(data);
            });
        } else {
            defer.resolve();
        }
        return defer.promise;
    }
}

export default APIService;